import type { RestrictionsRESTObjForContentApi } from './restrictionsRESTUtils.ts';

/**
 * Sanitizes restrictions object for GraphQL compatibility by removing unsupported fields.
 *
 * CONTEXT: Teams as principals Migration Issue
 * When Teams were migrated as principals, group included a `usageType` field (typically "USERBASE_GROUP")
 *
 * Problem:
 * - TypeScript RESTGroup type includes: { type, name, id, usageType }
 * - GraphQL RESTGroup input type only accepts: { type, name, id }
 * - Sending `usageType` to GraphQL mutations causes validation errors
 *
 * Solution:
 * This function removes the `usageType` field from all group objects before sending to GraphQL,
 * ensuring compatibility while preserving all other restriction data.
 *
 * USAGE:
 * Call this function on any restrictions object before passing to GraphQL mutations:
 * - experimentalPublishPageAsBlog (blog conversion)
 * - experimentalPublishEditorContent (regular publishing)
 */
export function sanitizeRestrictionsForGraphQL(
	data: RestrictionsRESTObjForContentApi,
): RestrictionsRESTObjForContentApi {
	// If there is no data to sanitize, just return it
	if (!data) return data;

	// Deep clone to avoid mutating the original data - with proper typing
	const sanitizedData: RestrictionsRESTObjForContentApi = JSON.parse(JSON.stringify(data));

	// Iterate through all restriction operations (read, update, etc.)
	(Object.keys(sanitizedData) as Array<keyof RestrictionsRESTObjForContentApi>).forEach(
		(operation) => {
			const contentRestriction = sanitizedData[operation];
			const groupResults = contentRestriction?.restrictions?.group?.results;
			if (Array.isArray(groupResults)) {
				groupResults.forEach((group) => {
					// Remove usageType field if it exists (Teams have this field)
					if ('usageType' in group) {
						Reflect.deleteProperty(group, 'usageType');
					}
				});
			}
		},
	);

	return sanitizedData;
}
